import { useEffect, useState, useCallback } from 'react';
import type { FC } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Typography,
  Box,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

export interface ScaEnrollmentButtonProps {
  /**
   * Whether SCA enrollment is complete
   */
  isEnrolled: boolean;
  /**
   * Callback to initiate enrollment, should return redirect URL
   */
  onEnroll: () => Promise<{ redirect_url: string | null }>;
  /**
   * Callback to refetch enrollment status
   */
  refetch: () => void;
  /**
   * Whether to show close button on dialog. Defaults to true.
   */
  showCloseButton?: boolean;
}

export const ScaEnrollmentButton: FC<ScaEnrollmentButtonProps> = ({
  isEnrolled,
  onEnroll,
  refetch,
  showCloseButton = true,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [searchParams] = useSearchParams();
  const controlStatus = searchParams.get('controlStatus');
  const isValidated = controlStatus === 'VALIDATED';

  const [fetchCount, setFetchCount] = useState(0);

  const clearIntervalAndNavigate = useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.delete('controlStatus');
    navigate(`${location.pathname}?${params.toString()}`, {
      replace: true,
    });
  }, [location, navigate]);

  const handleEnroll = useCallback(() => {
    onEnroll()
      .then((response) => {
        if (response.redirect_url) {
          window.location.href = response.redirect_url;
        }
      })
      .catch(() => {
        enqueueSnackbar(t('menu-bar.error'), {
          variant: 'error',
        });
      });
  }, [onEnroll, enqueueSnackbar, t]);

  useEffect(() => {
    if (controlStatus === 'FAILED') {
      enqueueSnackbar(t('sca-check-status.failed'), { variant: 'error' });
      clearIntervalAndNavigate();
    } else if (controlStatus === 'VALIDATED' && isEnrolled) {
      enqueueSnackbar(t('sca-check-status.success'), { variant: 'success' });
      clearIntervalAndNavigate();
    } else if (controlStatus === 'VALIDATED' && fetchCount >= 5) {
      enqueueSnackbar(t('sca-check-status.failed'), { variant: 'error' });
      clearIntervalAndNavigate();
    } else if (controlStatus === 'VALIDATED' && fetchCount < 5) {
      const interval = setInterval(() => {
        setFetchCount((prev) => prev + 1);
        refetch();
      }, 2000);
      return () => clearInterval(interval);
    }

    return undefined;
  }, [
    isEnrolled,
    controlStatus,
    fetchCount,
    t,
    enqueueSnackbar,
    refetch,
    clearIntervalAndNavigate,
  ]);

  return (
    <>
      <Dialog
        open={isValidated}
        onClose={showCloseButton ? clearIntervalAndNavigate : undefined}
        disableEscapeKeyDown={!showCloseButton}
      >
        <DialogContent>
          <Box position="relative">
            {showCloseButton && (
              <IconButton
                aria-label="close"
                onClick={clearIntervalAndNavigate}
                sx={{
                  position: 'absolute',
                  right: -8,
                  top: -8,
                }}
              >
                <CloseIcon />
              </IconButton>
            )}
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap={2}
              pt={showCloseButton ? 2 : 0}
            >
              <Typography variant="h4">
                {t('menu-bar.sca-enrollment-processing')}
              </Typography>
              <Typography variant="body1">
                {t('menu-bar.sca-enrollment-processing-description')}
              </Typography>
              <CircularProgress color="primary" size={40} />
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      <Button
        variant="contained"
        color="primary"
        disabled={isValidated}
        onClick={handleEnroll}
      >
        {t('menu-bar.sca-enrollment')}
      </Button>
    </>
  );
};
